import { useEffect, useMemo, useState } from "react";
import { adminFurniture } from "../../services/adminApi";
import {
  Search,
  Plus,
  RefreshCw,
  Grid3X3,
  List,
  MoreVertical,
  Image as ImageIcon,
  Trash2,
  Edit3,
  Sofa,
  Layers,
  AlertTriangle,
  CheckCircle2,
} from "lucide-react";

import FurnitureDrawer from "./FurnitureDrawer";

const furnitureCategories = [
  { value: "", label: "All Categories" },
  { value: "seating", label: "Seating" },
  { value: "table", label: "Tables" },
  { value: "storage", label: "Storage" },
  { value: "bed", label: "Beds" },
  { value: "appliance", label: "Appliances" },
  { value: "office", label: "Office" },
  { value: "gym", label: "Gym" },
  { value: "decor", label: "Decor" },
  { value: "other", label: "Other" },
];
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
}

export default function FurniturePage() {
  const isMobile = useIsMobile();
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [active, setActive] = useState("true"); // "true" | "false" | ""
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);

  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);

  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState("grid"); // grid | table

  const [selected, setSelected] = useState([]);

  async function load() {
    setLoading(true);
    try {
      const r = await adminFurniture.list({ q, category, active });
      setRows(r?.items || []);
      setTotal(r?.total || 0);
    } catch (e) {
      console.error("Failed to load furniture:", e);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, category, active]);

  const stats = useMemo(() => {
    const totalCount = total || rows.length;
    const activeCount = rows.filter((x) => x.active).length;
    const missingPlan = rows.filter((x) => !x.plan?.url).length;
    const missingMesh = rows.filter((x) => !x.model?.meshUrl).length;
    return { totalCount, activeCount, missingPlan, missingMesh };
  }, [rows, total]);

  const handleSelect = (id, checked) => {
    setSelected((prev) =>
      checked ? [...prev, id] : prev.filter((x) => x !== id),
    );
  };

  const handleSelectAll = (checked) => {
    setSelected(checked ? rows.map((x) => x.id) : []);
  };

  async function handleDelete(id) {
    if (!window.confirm("Delete this furniture item? This cannot be undone."))
      return;
    try {
      setLoading(true);
      await adminFurniture.remove(id);
      await load();
    } catch (e) {
      console.error(e);
      alert("Failed to delete furniture. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  async function handleBulkDelete() {
    if (selected.length === 0) return;
    if (
      !window.confirm(
        `Delete ${selected.length} selected items? This cannot be undone.`,
      )
    )
      return;

    try {
      setLoading(true);
      // If you later implement bulk-delete endpoint, switch to it:
      // await adminFurniture.bulkDelete(selected);
      await Promise.all(selected.map((id) => adminFurniture.remove(id)));
      setSelected([]);
      await load();
    } catch (e) {
      console.error(e);
      alert("Bulk delete failed. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-3">
            <div>
              <h1
                className="text-3xl font-bold text-gray-900 sm:text-4xl"
                style={{
                  fontSize: isMobile ? 22 : 28,
                  fontWeight: 800,
                  margin: 0,
                }}
              >
                Furniture Library
              </h1>
              <p className="text-gray-600 mt-1 hidden sm:block">
                Manage 2D plan icons + 3D models for in-building placement
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 flex-shrink-0">
          <button
            onClick={load}
            disabled={loading}
            className="p-2 bg-white border border-gray-200 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
            title="Refresh"
          >
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
          </button>

          <button
            className="px-4 py-2 bg-black text-white rounded-md transition-colors flex items-center gap-2 shadow-sm"
            onClick={() => {
              setEditId(null);
              setOpen(true);
            }}
          >
            <Plus size={16} />
            {isMobile ? "Add" : "Add Furniture"}
          </button>
        </div>
      </div>

      {/* Stats - Desktop */}
      <div className="hidden sm:grid grid-cols-4 gap-4">
        <StatTile
          label="Total"
          value={stats.totalCount}
          icon={Layers}
          tone="blue"
        />
        <StatTile
          label="Active"
          value={stats.activeCount}
          icon={CheckCircle2}
          tone="green"
        />
        <StatTile
          label="Missing Plan PNG"
          value={stats.missingPlan}
          icon={AlertTriangle}
          tone="amber"
        />
        <StatTile
          label="Missing 3D Mesh"
          value={stats.missingMesh}
          icon={AlertTriangle}
          tone="amber"
        />
      </div>

      {/* Stats - Mobile compact single row */}
      <div className="grid grid-cols-4 gap-1.5 sm:hidden">
        <MobileStatTile label="Total" value={stats.totalCount} tone="blue" />
        <MobileStatTile label="Active" value={stats.activeCount} tone="green" />
        <MobileStatTile
          label="No Plan"
          value={stats.missingPlan}
          tone="amber"
        />
        <MobileStatTile
          label="No Mesh"
          value={stats.missingMesh}
          tone="amber"
        />
      </div>

      {/* Filters */}
      <div className="bg-white rounded-md border border-gray-200 shadow-sm p-4 sm:p-6">
        <div className="flex flex-col gap-3">
          {/* Search - full width */}
          <div className="relative">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              size={18}
            />
            <input
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
              placeholder="Search by name or code..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>

          {/* Category + Status on same row; view toggle desktop only */}
          <div className="flex items-center gap-2">
            {/* Category */}
            <select
              className="flex-1 sm:flex-none sm:min-w-48 px-3 py-2.5 sm:py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors bg-white text-sm"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              {furnitureCategories.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>

            {/* Active */}
            <select
              className="flex-1 sm:flex-none sm:min-w-40 px-3 py-2.5 sm:py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors bg-white text-sm"
              value={active}
              onChange={(e) => setActive(e.target.value)}
            >
              <option value="">All Status</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>

            {/* View toggle - desktop only */}
            <div className="hidden sm:flex border border-gray-300 rounded-md p-1 ml-auto">
              <button
                onClick={() => setViewMode("grid")}
                className={`p-2 rounded ${
                  viewMode === "grid"
                    ? "bg-black text-white"
                    : "text-gray-600 hover:text-gray-900"
                } transition-colors`}
                title="Grid"
              >
                <Grid3X3 size={16} />
              </button>
              <button
                onClick={() => setViewMode("table")}
                className={`p-2 rounded ${
                  viewMode === "table"
                    ? "bg-black text-white"
                    : "text-gray-600 hover:text-gray-900"
                } transition-colors`}
                title="Table"
              >
                <List size={16} />
              </button>
            </div>
          </div>
        </div>

        {/* Bulk actions */}
        {selected.length > 0 && (
          <div className="mt-4 p-4 bg-indigo-50 border border-indigo-200 rounded-md">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-indigo-800">
                {selected.length} selected
              </span>
              <div className="flex gap-2">
                <button
                  onClick={handleBulkDelete}
                  className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700 transition-colors"
                >
                  Delete Selected
                </button>
                <button
                  onClick={() => setSelected([])}
                  className="px-3 py-1 border border-gray-300 rounded text-sm hover:bg-white transition-colors"
                >
                  Clear
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Loading */}
      {loading && (
        <div className="bg-white rounded-md border border-gray-200 shadow-sm p-12">
          <div className="flex items-center justify-center gap-2 text-gray-600">
            <RefreshCw size={20} className="animate-spin" />
            Loading furniture...
          </div>
        </div>
      )}

      {/* Content - Mobile card list */}
      {!loading && (
        <div className="sm:hidden flex flex-col gap-3">
          {rows.map((f) => (
            <FurnitureMobileCard
              key={f.id}
              item={f}
              onEdit={() => {
                setEditId(f.id);
                setOpen(true);
              }}
              onDelete={() => handleDelete(f.id)}
            />
          ))}
        </div>
      )}

      {/* Content - Desktop grid/table */}
      {!loading && (
        <div className="hidden sm:block">
          {viewMode === "grid" ? (
            <FurnitureGrid
              items={rows}
              selected={selected}
              onSelect={handleSelect}
              onEdit={(id) => {
                setEditId(id);
                setOpen(true);
              }}
              onDelete={handleDelete}
            />
          ) : (
            <FurnitureTable
              items={rows}
              selected={selected}
              onSelect={handleSelect}
              onSelectAll={handleSelectAll}
              onEdit={(id) => {
                setEditId(id);
                setOpen(true);
              }}
              onDelete={handleDelete}
            />
          )}
        </div>
      )}

      {/* Empty */}
      {!loading && rows.length === 0 && (
        <div className="bg-white rounded-md border border-gray-200 shadow-sm p-12 text-center">
          <Sofa className="mx-auto text-gray-300 mb-4" size={48} />
          <div className="text-gray-700 font-medium mb-2">
            No furniture found
          </div>
          <div className="text-gray-500 text-sm mb-4">
            {q || category || active !== "true"
              ? "Try adjusting your filters."
              : "Get started by adding your first furniture item."}
          </div>
          {!q && !category && active === "true" && (
            <button
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
              onClick={() => {
                setEditId(null);
                setOpen(true);
              }}
            >
              Add Your First Furniture
            </button>
          )}
        </div>
      )}

      {open && (
        <FurnitureDrawer
          id={editId}
          onClose={async (changed) => {
            setOpen(false);
            if (changed) await load();
          }}
        />
      )}
    </div>
  );
}

function StatTile({ label, value, icon: Icon, tone = "blue" }) {
  const toneMap = {
    blue: "from-blue-50 to-indigo-50 border-blue-200 text-blue-700",
    green: "from-emerald-50 to-green-50 border-emerald-200 text-emerald-700",
    amber: "from-gray-100 to-gray-50 border-gray-300 text-gray-700",
  };

  return (
    <div className={`bg-gradient-to-r ${toneMap[tone]} rounded-md border p-4`}>
      <div className="flex items-center justify-between">
        <div className="min-w-0">
          <p className="text-xs font-medium truncate">{label}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
        </div>
        <div className="w-10 h-10 bg-white/70 rounded-md flex items-center justify-center">
          <Icon size={18} className="text-gray-700" />
        </div>
      </div>
    </div>
  );
}

function MobileStatTile({ label, value, tone = "blue" }) {
  const toneMap = {
    blue: "bg-blue-50 border-blue-200 text-blue-700",
    green: "bg-emerald-50 border-emerald-200 text-emerald-700",
    amber: "bg-gray-100 border-gray-300 text-gray-700",
  };
  return (
    <div className={`${toneMap[tone]} border rounded-md p-2 text-center`}>
      <p className="text-lg font-bold text-gray-900 leading-tight">{value}</p>
      <p className="text-xs leading-tight mt-0.5 truncate">{label}</p>
    </div>
  );
}

function FurnitureMobileCard({ item, onEdit, onDelete }) {
  const thumbUrl = item.model?.thumbUrl || null;
  const planUrl = item.plan?.url || null;
  const meshUrl = item.model?.meshUrl || null;

  return (
    <div className="bg-white rounded-md border border-gray-200 shadow-sm p-3 flex items-center gap-3">
      {/* Thumbnail */}
      <div className="w-14 h-14 flex-shrink-0 bg-gray-50 border border-gray-200 rounded-md flex items-center justify-center overflow-hidden">
        {thumbUrl ? (
          <img
            src={thumbUrl}
            alt={item.name}
            className="w-full h-full object-contain p-1"
          />
        ) : (
          <ImageIcon className="text-gray-300" size={22} />
        )}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="font-semibold text-gray-900 text-sm truncate">
            {item.name}
          </span>
          <span className="text-xs text-gray-500 capitalize">
            {item.category}
          </span>
        </div>
        <p className="text-xs text-gray-400 font-mono truncate">{item.code}</p>
        <div className="flex items-center gap-1.5 mt-1 flex-wrap">
          <Badge tone={item.active ? "green" : "gray"}>
            {item.active ? "Active" : "Inactive"}
          </Badge>
          {!planUrl && <Badge tone="amber">No Plan</Badge>}
          {!meshUrl && <Badge tone="amber">No Mesh</Badge>}
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-col gap-1.5 flex-shrink-0">
        <button
          onClick={onEdit}
          className="p-2 text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-md transition-colors"
          title="Edit"
        >
          <Edit3 size={16} />
        </button>
        <button
          onClick={onDelete}
          className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
          title="Delete"
        >
          <Trash2 size={16} />
        </button>
      </div>
    </div>
  );
}

function FurnitureGrid({ items, selected, onSelect, onEdit, onDelete }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-6">
      {items.map((f) => (
        <FurnitureCard
          key={f.id}
          item={f}
          isSelected={selected.includes(f.id)}
          onSelect={onSelect}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      ))}
    </div>
  );
}

function FurnitureCard({ item, isSelected, onSelect, onEdit, onDelete }) {
  const [showActions, setShowActions] = useState(false);

  const planUrl = item.plan?.url || null;
  const thumbUrl = item.model?.thumbUrl || null;
  const meshUrl = item.model?.meshUrl || null;

  const sizeW = item.size3d?.w;
  const sizeD = item.size3d?.d;
  const sizeH = item.size3d?.h;

  const planDispW = item.plan?.displayPx?.w;
  const planDispH = item.plan?.displayPx?.h;

  return (
    <div
      className={`bg-white rounded-md border shadow-sm overflow-hidden transition-all hover:shadow-md ${
        isSelected
          ? "border-indigo-500 ring-2 ring-indigo-500 ring-opacity-20"
          : "border-gray-200"
      }`}
    >
      {/* preview */}
      <div className="relative aspect-[4/3] bg-gray-50">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={(e) => onSelect(item.id, e.target.checked)}
          className="absolute top-3 left-3 w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 z-10"
        />

        {thumbUrl ? (
          <img
            src={thumbUrl}
            alt={item.name}
            className="w-full h-full object-contain p-4"
          />
        ) : thumbUrl ? (
          <img
            src={thumbUrl}
            alt={item.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ImageIcon className="text-gray-300" size={44} />
          </div>
        )}

        {/* badge row */}
        <div className="absolute bottom-3 left-3 flex items-center gap-2">
          <Badge tone={item.active ? "green" : "gray"}>
            {item.active ? "Active" : "Inactive"}
          </Badge>
          {!planUrl && <Badge tone="amber">No plan PNG</Badge>}
          {!meshUrl && <Badge tone="amber">No mesh</Badge>}
        </div>

        {/* actions */}
        <div className="absolute top-3 right-3">
          <button
            onClick={() => setShowActions((s) => !s)}
            className="w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-sm hover:shadow-md transition-shadow"
            title="Actions"
          >
            <MoreVertical size={16} />
          </button>

          {showActions && (
            <div className="absolute right-0 top-full mt-1 w-36 bg-white border border-gray-200 rounded-md shadow-lg z-20">
              <div className="py-1">
                <button
                  onClick={() => {
                    setShowActions(false);
                    onEdit(item.id);
                  }}
                  className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  <Edit3 size={14} />
                  Edit
                </button>
                <button
                  onClick={() => {
                    setShowActions(false);
                    onDelete(item.id);
                  }}
                  className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-700 hover:bg-red-50"
                >
                  <Trash2 size={14} />
                  Delete
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* content */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="font-semibold text-gray-900 truncate">
              {item.name}
            </h3>
            <p className="text-sm text-gray-500 font-mono truncate">
              {item.code}
            </p>
          </div>
          <span className="text-xs text-gray-500 capitalize whitespace-nowrap">
            {item.category}
          </span>
        </div>

        <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-gray-600">
          <div className="bg-gray-50 rounded-md p-2">
            <div className="text-gray-400">3D Size</div>
            <div className="font-mono">
              {num(sizeW)} × {num(sizeD)} m
            </div>
          </div>

          <div className="bg-gray-50 rounded-md p-2">
            <div className="text-gray-400">Height</div>
            <div className="font-mono">
              {sizeH != null ? `${num(sizeH)} m` : "—"}
            </div>
          </div>

          <div className="bg-gray-50 rounded-md p-2 col-span-2">
            <div className="text-gray-400">2D Display</div>
            <div className="font-mono">
              {planDispW != null && planDispH != null
                ? `${planDispW} × ${planDispH} px`
                : "—"}
            </div>
          </div>
        </div>

        {item.tags?.length ? (
          <div className="mt-3 flex flex-wrap gap-2">
            {item.tags.slice(0, 3).map((t) => (
              <span
                key={t}
                className="px-2 py-1 rounded-full text-xs bg-indigo-50 text-indigo-700 border border-indigo-100"
              >
                {t}
              </span>
            ))}
            {item.tags.length > 3 && (
              <span className="text-xs text-gray-500">
                +{item.tags.length - 3}
              </span>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function FurnitureTable({
  items,
  selected,
  onSelect,
  onSelectAll,
  onEdit,
  onDelete,
}) {
  return (
    <div className="bg-white rounded-md border border-gray-200 shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-100">
            <tr>
              <th className="p-4 text-left">
                <input
                  type="checkbox"
                  checked={selected.length === items.length && items.length > 0}
                  onChange={(e) => onSelectAll(e.target.checked)}
                  className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                />
              </th>
              <th className="p-4 text-left font-medium text-gray-900">
                Furniture
              </th>
              <th className="p-4 text-left font-medium text-gray-900">Code</th>
              <th className="p-4 text-left font-medium text-gray-900">
                Category
              </th>
              <th className="p-4 text-left font-medium text-gray-900">
                3D Size (m)
              </th>
              <th className="p-4 text-left font-medium text-gray-900">
                2D Display (px)
              </th>
              <th className="p-4 text-left font-medium text-gray-900">Plan</th>
              <th className="p-4 text-left font-medium text-gray-900">Mesh</th>
              <th className="p-4 text-left font-medium text-gray-900">
                Status
              </th>
              <th className="p-4 text-right font-medium text-gray-900">
                Actions
              </th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-100">
            {items.map((f) => {
              const planUrl = f.plan?.url || null;
              const thumbUrl = f.model?.thumbUrl || null;
              const meshUrl = f.model?.meshUrl || null;

              const sizeW = f.size3d?.w;
              const sizeD = f.size3d?.d;

              const dispW = f.plan?.displayPx?.w;
              const dispH = f.plan?.displayPx?.h;

              return (
                <tr key={f.id} className="hover:bg-gray-50 transition-colors">
                  <td className="p-4">
                    <input
                      type="checkbox"
                      checked={selected.includes(f.id)}
                      onChange={(e) => onSelect(f.id, e.target.checked)}
                      className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                    />
                  </td>

                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      {thumbUrl ? (
                        <img
                          src={thumbUrl}
                          alt={f.name}
                          className="w-12 h-12 object-contain bg-gray-50 border border-gray-200 rounded-md p-1"
                        />
                      ) : thumbUrl ? (
                        <img
                          src={thumbUrl}
                          alt={f.name}
                          className="w-12 h-12 object-cover rounded-md"
                        />
                      ) : (
                        <div className="w-12 h-12 bg-gray-100 rounded-md flex items-center justify-center">
                          <ImageIcon className="text-gray-300" size={20} />
                        </div>
                      )}

                      <div className="min-w-0">
                        <div className="font-medium text-gray-900 truncate">
                          {f.name}
                        </div>
                        <div className="text-sm text-gray-500">
                          ID: {f.id.slice(0, 8)}…
                        </div>
                      </div>
                    </div>
                  </td>

                  <td className="p-4 font-mono text-sm">{f.code}</td>
                  <td className="p-4 capitalize text-gray-700">{f.category}</td>

                  <td className="p-4 font-mono text-sm">
                    {num(sizeW)} × {num(sizeD)}
                  </td>

                  <td className="p-4 font-mono text-sm">
                    {dispW != null && dispH != null ? `${dispW}×${dispH}` : "—"}
                  </td>

                  <td className="p-4">
                    {planUrl ? (
                      <Badge tone="green">OK</Badge>
                    ) : (
                      <Badge tone="amber">Missing</Badge>
                    )}
                  </td>
                  <td className="p-4">
                    {meshUrl ? (
                      <Badge tone="green">OK</Badge>
                    ) : (
                      <Badge tone="amber">Missing</Badge>
                    )}
                  </td>

                  <td className="p-4">
                    {f.active ? (
                      <Badge tone="green">Active</Badge>
                    ) : (
                      <Badge tone="gray">Inactive</Badge>
                    )}
                  </td>

                  <td className="p-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => onEdit(f.id)}
                        className="p-2 text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-md transition-colors"
                        title="Edit"
                      >
                        <Edit3 size={16} />
                      </button>
                      <button
                        onClick={() => onDelete(f.id)}
                        className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Badge({ tone = "gray", children }) {
  const tones = {
    gray: "bg-gray-100 text-gray-700 border-gray-200",
    green: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber: "bg-gray-100 text-gray-700 border-gray-300",
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${tones[tone]}`}
    >
      {children}
    </span>
  );
}

function num(v) {
  if (v == null || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return n.toFixed(2);
}
